import React, { useState, useEffect, useRef } from 'react';
import { Section, Container } from 'components/shared';
import { getFieldValue, Placeholder } from '@sitecore-jss/sitecore-jss-react';
import { AspectRatio } from 'react-aspect-ratio';
import { Button, Copy, ImageVideo } from 'components/components/atoms';
import ResizeIcon from 'components/icons/Resize';
import CloseIcon from 'components/icons/Close';
import { StyledModal } from 'components/shared';
import breakpoints from 'utils/breakpoints';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import styled from 'styled-components';
import tw from 'twin.macro';
import { isValidUrl } from 'utils/validateUrlSrc';
import { isValidTitle } from 'utils/validateTitle';
import { getScrFromIframe } from 'utils/getScrFromIframe';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import cx from 'classnames';

const StyledHeading = styled.h2`
  margin-bottom: 0px;
  font-size: 1.625rem;
  @media ${breakpoints.lg} {
    font-size: 2.25rem;
  }
  @media ${breakpoints.xl} {
    font-size: 2.875rem;
  }
`;
const StyledCopy = styled(Copy)`
  @media ${breakpoints.lg} {
    width: 40rem;
  }
`;
const IframeContainer = styled.div`
  iframe {
    height: 100%;
  }
`;

const Modal = styled(StyledModal)`
  ${tw`select-none w-full h-full flex justify-center`}
  z-index: 94;
`;
const ModalContent = styled.div`
  width: ${({ isMobile }) => (isMobile ? '89vw' : 'max(790px, 90vw)')};
  height: 95vh;
  display: flex;
  flex-direction: column;
`;
const IframeModalContainer = styled.div`
  iframe {
    width: 100%;
    height: 150vw;
    @media ${breakpoints.md} {
      height: inherit;
    }
  }
`;

const hasStaticCarousel = (rendering) => {
  try {
    const staticCarousel = rendering.placeholders['static-carousel'][0];
    if (!staticCarousel) return false;

    const { fields } = staticCarousel;
    const media = getFieldValue(fields, 'media');

    return media.length > 0;
  } catch (e) {
    return false;
  }
};

const isValidIframe = (iframe) => {
  if (iframe) {
    const hasIframe = iframe.includes('iframe');
    if (hasIframe) {
      let src = iframe.replace(/\n/g, '');
      const regex = /<iframe.*?src='(.*?)'/;
      const iframeSrc = regex.exec(src)[1];
      return isValidUrl(iframeSrc);
    }
  }
  return false;
};

const InteractiveSitemap = ({ fields, rendering }) => {
  // Fields
  const title = getFieldValue(fields, 'title');
  const image = getFieldValue(fields, 'image');
  const iframeField = getFieldValue(fields, 'iframe');
  const iframe = isValidIframe(iframeField) && iframeField;
  const subtitle = getFieldValue(fields, 'subtitle');
  const pdfFile = getFieldValue(fields, 'pdf');
  const isFullWidthPage = getFieldValue(fields, 'fullwidth') || false;
  const mobileCTA = getFieldValue(fields, 'mobileCTA') || 'View';
  const { componentName } = rendering;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isEndOfModalIframe, setIsEndOfModalIframe] = useState(false);
  const showCarousel = hasStaticCarousel(rendering);

  const iframeContainerRef = useRef();
  const iframeModalContainerRef = useRef();
  const closeIframeModalRef = useRef();
  const expandedButtonRef = useRef();

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const {
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
    showNewSchemaColor,
  } = useNewColorSchemaOptions();

  useEffect(() => {
    setTimeout(() => {
      if (closeIframeModalRef.current) {
        const element = closeIframeModalRef.current;
        if (element) {
          element.focus();
        }
      }
    }, 0);
  }, [isOpenModal]);

  useEffect(() => {
    const element = closeIframeModalRef.current;
    if (isEndOfModalIframe && element) {
      element.focus();
    }
  }, [isEndOfModalIframe]);

  useEffect(() => {
    setTimeout(() => {
      if (iframeContainerRef.current) {
        const element = iframeContainerRef.current;
        const iframe = element.children[0];
        if (iframe) {
          iframe.tabIndex = 0;
        }
      }
    }, 0);
  }, [iframeContainerRef.current]);

  const handleContainerFocus = () => {
    if (iframeContainerRef.current) {
      const element = iframeContainerRef.current;
      const iframe = element.children[0];
      if (iframe) {
        iframe.focus();
      }
    }
  };
  const handleModalContainerFocus = () => {
    if (iframeModalContainerRef.current) {
      const element = iframeModalContainerRef.current;
      const iframe = element.children[0];
      if (iframe) {
        iframe.focus();
      }
    }
  };

  if (
    !iframe &&
    !showCarousel &&
    !(image && ((isMobile ? image.srcMobile : image.srcDesktop) || image.src))
  ) {
    return '';
  }

  return (
    <Section id={'sitemap'} tabIndex='-1' className='pt-0 lg:pt-0'>
      <Container className='flex flex-col items-center bg-light-blue md:bg-white'>
        {isValidTitle(title) && (
          <StyledHeading
            className={cx(
              'font-bold leading-tiny font-trade-gothic-20 mt-6 md:mt-0 mb-6 md:mb-10-mattamy text-center',
              {
                'text-mattamy-blue': !showNewSchemaColor,
                'text-black': showNewSchemaColor,
              },
            )}
          >
            {title}
          </StyledHeading>
        )}
        <StyledCopy
          className='md:text-lg text-center md:pb-8 '
          color={showNewSchemaColor ? '#000' : 'blue'}
        >
          {subtitle}
        </StyledCopy>
        {isMobile && iframe && (
          <Button
            variant={'filled'}
            color={'mattamy-blue'}
            className='mt-8 mb-6 tracking-wider'
            onClick={(e) => {
              setIsOpenModal(true);
            }}
            aria-label={mobileCTA}
            ref={expandedButtonRef}
          >
            {mobileCTA}
          </Button>
        )}

        {!isFullWidthPage && iframe && !isMobile && (
          <div className='w-full'>
            <button
              className='absolute z-20 top-0 right-0 ml-2 mt-24 text-mattamy-blue'
              onClick={(e) => {
                setIsOpenModal(true);
              }}
              aria-label={'Resize Iframe'}
              aria-describedby={'Resize Iframe button'}
              aria-haspopup='true'
              ref={expandedButtonRef}
            >
              <ResizeIcon className='w-8' />
            </button>
            <AspectRatio ratio='1200/650' className='w-full'>
              <IframeContainer
                tabIndex='0'
                ref={iframeContainerRef}
                className='w-full rounded overflow-hidden'
                dangerouslySetInnerHTML={{
                  __html: iframe
                    ? "<iframe class='w-full h-full' src='" +
                      getScrFromIframe(iframe) +
                      "' title='" +
                      title +
                      "'/>"
                    : '',
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Tab' && !event.shiftKey) {
                    event.preventDefault();
                    handleContainerFocus();
                  }
                }}
              ></IframeContainer>
            </AspectRatio>
          </div>
        )}
      </Container>

      {isFullWidthPage && iframe && !isMobile && (
        <AspectRatio ratio='1400/770' className='w-full overflow-hidden'>
          <IframeContainer
            ref={iframeContainerRef}
            className='w-full'
            dangerouslySetInnerHTML={{
              __html: iframe
                ? "<iframe class='w-full h-full' src='" +
                  getScrFromIframe(iframe) +
                  "' title='" +
                  title +
                  "'/>"
                : '',
            }}
          ></IframeContainer>
        </AspectRatio>
      )}

      {!iframe && showCarousel && (
        <Placeholder name='static-carousel' rendering={rendering} />
      )}
      {!iframe && !showCarousel && (
        <Container className=' bg-light-blue md:bg-white pb-4' tabIndex='0'>
          <AspectRatio ratio='1800/1200' className='w-full' tabIndex='0'>
            {pdfFile && pdfFile.href ? (
              <a
                href={pdfFile.href}
                target={'_blank'}
                aria-label={title + '. Opens in new tab'}
                rel='noreferrer'
              >
                <ImageVideo {...image} displayOption='Image' />
              </a>
            ) : (
              <ImageVideo {...image} displayOption='Image' />
            )}
          </AspectRatio>
        </Container>
      )}

      {iframe && (!isFullWidthPage || isMobile) && (
        <div>
          <Modal isOpen={isOpenModal} data-aos='fade' centered>
            <ModalContent
              isMobile={isMobile}
              className='flex flex-col justify-center items-center'
              aria-modal='true'
              role='dialog'
            >
              <div
                className='w-full md:h-full md:my-8 relative flex flex-col items-center bg-white
              rounded  p-2 md:p-8'
              >
                <button
                  className='absolute top-0 right-0 -mt-8 md:mt-1 mb-10 mr-0 md:mr-1 z-10 transform hover:scale-110
                transition-all duration-200 text-white md:text-dark-gray-bg'
                  onClick={(e) => {
                    setIsOpenModal(false);

                    const element = expandedButtonRef.current;
                    if (element) {
                      element.focus();
                    }
                  }}
                  onFocus={(e) => {
                    setIsEndOfModalIframe(false);
                  }}
                  ref={closeIframeModalRef}
                  aria-label={'Close iframe modal'}
                  aria-describedby={'Close iframe modal button'}
                >
                  <CloseIcon className='w-8' />
                </button>
                <IframeModalContainer
                  id='sitemapmodalid'
                  tabIndex='0'
                  ref={iframeModalContainerRef}
                  className='w-full h-full pr-0 '
                  dangerouslySetInnerHTML={{
                    __html: iframe
                      ? "<iframe class='w-full h-full' src='" +
                        getScrFromIframe(iframe) +
                        "' title='" +
                        title +
                        "'/>"
                      : '',
                  }}
                  onFocus={handleModalContainerFocus}
                />
              </div>
              <div
                tabIndex={0}
                onFocus={(e) => {
                  setIsEndOfModalIframe(true);
                }}
              />
            </ModalContent>
          </Modal>
        </div>
      )}
    </Section>
  );
};

export default InteractiveSitemap;
